import { createReducerBySpec } from '@Utils/Redux/reducerFactory';
import { generateRelativeIndex } from '@Containers/PatentFamilyClustering/utils';
import {
  RESET_PATENT_FAMILY_CLUSTERING_STATE,
  INIT_PATENT_FAMILY_GROUPS,
  EXPAND_PATENT_FAMILY_GROUP,
  SET_PAGE_NUMBER,
  LOAD_PATENT_FAMILY_MEMBERS_START,
  LOAD_PATENT_FAMILY_MEMBERS_SUCCESS,
} from './constants';

export const initialState = {
  /*
    Map format:
    {
      [familyId]: {
        patentFamilyMembers: Array<Object>,
        pageNumber: Number,
        isExpanded: Number,
        isLoading: Boolean
      }
    }
  * */
  patentFamilyGroupsMap: {},
};

const spec = {
  [RESET_PATENT_FAMILY_CLUSTERING_STATE]: () => initialState,
  [INIT_PATENT_FAMILY_GROUPS]: (state, action) => {
    const documents = action.payload;
    const { patentFamilyGroupsMap } = state;

    return {
      ...state,
      patentFamilyGroupsMap: {
        ...patentFamilyGroupsMap,
        ...documents.reduce((acc, document) => {
          const { index, familyId, patentFamilyGroup } = document;

          // Not a patent family group
          if (!familyId) {
            return acc;
          }

          // Update absoluteIndex and relativeIndex for main patent family member,
          // absoluteIndex: in processPatentFamilyGroups it was set to [1-2] for whole group, now for main family member it should be 1
          // relativeIndex: for main item in the 1st group it will be 1/1, for main item in 2nd group - 2/1 and so on
          const mainFamilyMember = {
            ...document,
            absoluteIndex: Number(patentFamilyGroup.position),
            relativeIndex: generateRelativeIndex(index, 1),
          };

          return {
            ...acc,
            [familyId]: {
              patentFamilyMembers: [mainFamilyMember],
              pageNumber: 1,
              isExpanded: false,
              isLoading: false,
            },
          };
        }, {}),
      },
    };
  },
  [EXPAND_PATENT_FAMILY_GROUP]: (state, action) => {
    const { document, isExpanded } = action.payload;
    const { familyId } = document;
    const { patentFamilyGroupsMap } = state;
    const group = state.patentFamilyGroupsMap[familyId];
    return {
      ...state,
      patentFamilyGroupsMap: {
        ...patentFamilyGroupsMap,
        [familyId]: {
          ...group,
          isExpanded,
        },
      },
    };
  },
  [SET_PAGE_NUMBER]: (state, action) => {
    const { document, pageNumber } = action.payload;
    const { familyId } = document;
    const { patentFamilyGroupsMap } = state;
    const group = state.patentFamilyGroupsMap[familyId];
    return {
      ...state,
      patentFamilyGroupsMap: {
        ...patentFamilyGroupsMap,
        [familyId]: {
          ...group,
          pageNumber,
        },
      },
    };
  },
  [LOAD_PATENT_FAMILY_MEMBERS_START]: (state, action) => {
    const { document, expand } = action.payload;
    const { familyId } = document;
    const { patentFamilyGroupsMap } = state;
    const group = state.patentFamilyGroupsMap[familyId];
    return {
      ...state,
      patentFamilyGroupsMap: {
        ...patentFamilyGroupsMap,
        [familyId]: {
          ...group,
          isLoading: true,
          ...(!expand && { patentFamilyMembers: [] }),
        },
      },
    };
  },
  [LOAD_PATENT_FAMILY_MEMBERS_SUCCESS]: (state, action) => {
    const { document, expand, patentFamilyMembers } = action.payload;
    const { familyId } = document;
    const { patentFamilyGroupsMap } = state;
    const group = state.patentFamilyGroupsMap[familyId];
    return {
      ...state,
      patentFamilyGroupsMap: {
        ...patentFamilyGroupsMap,
        [familyId]: {
          ...group,
          isLoading: false,
          ...(!expand ? { patentFamilyMembers } : { patentFamilyMembers: group.patentFamilyMembers.concat(patentFamilyMembers) }),
        },
      },
    };
  },
};

export default createReducerBySpec(spec, initialState);
